import { queryOptions } from "@tanstack/react-query";
import type {
  ConnectedAccountItem,
  ConnectedAccountsResponse,
} from "@/app/oauth/types";
import type { ScopedBinding } from "@/app/types";
import type { ApiClient } from "@/lib/api";
import { getEngineApi } from "@/lib/api";
import type { OrgProjectScope } from "@/lib/resource-context";
import { connectedAccountsKeys } from "./query-keys";

const fetchConnectedAccounts = async ({
  client,
  oauthProviderId,
  userId,
  limit = 1000,
  offset = 0,
}: {
  client: ApiClient;
  oauthProviderId?: string;
  userId?: string;
  limit?: number;
  offset?: number;
}): Promise<ConnectedAccountItem[]> => {
  const { data } = await client.get<ConnectedAccountsResponse>(
    "/user_connections",
    {
      params: {
        limit,
        offset,
        ...(oauthProviderId && {
          provider_id: oauthProviderId,
        }),
        ...(userId && { user_id: userId }),
      },
    }
  );
  return data?.items ?? [];
};

export const connectedAccountsQueryOptions = ({
  binding = "project",
  oauthProviderId,
  userId,
  limit = 1000,
  offset = 0,
  scope,
}: {
  binding?: ScopedBinding;
  oauthProviderId?: string;
  userId?: string;
  limit?: number;
  offset?: number;
  scope: OrgProjectScope;
}) => {
  const client = getEngineApi(scope, binding);

  return queryOptions<ConnectedAccountItem[]>({
    queryKey: connectedAccountsKeys.list({
      binding,
      scope,
      oauthProviderId,
      userId,
    }),
    queryFn: () =>
      fetchConnectedAccounts({
        client,
        oauthProviderId,
        userId,
        limit,
        offset,
      }),
  });
};
